// Menu logic for the admin app view's "More" sheet (components/common/AdminAppLayout.js).
// Kept free of React so it can be tested: it turns the nav tree into the sections the sheet
// draws, and holds the per-device pins / recent screens.

const PIN_KEY = 'appView.pins';
const RECENT_KEY = 'appView.recent';
const MAX_PINS = 20;
const MAX_RECENT = 4;

// GTPL screens used daily stay visible; the rest sit behind "Show more" in the GTPL category.
const GTPL_MAIN = ['/gtpl', '/gtpl-saathi-subscriber', '/gtpl-saathi-multi-renew', '/gtpl-box-stock', '/gtpl-expiry-check', '/gtpl-90-days',
  '/gtpl-saathi-hardware-replacement', '/gtpl-vs-crm'];

// The desktop sidebar's section headers (navConfig.js) become the phone's six categories, same order.
const CATEGORY_TITLES = { DAILY: 'Daily', 'CUSTOMERS & BILLING': 'Customer & Billing', REPORTS: 'Reports', NETWORK: 'Network', GTPL: 'GTPL', ADMIN: 'Admin' };

const leafOf = (item, groupKey) => ({ id: item.to, to: item.to, label: item.label, icon: item.icon, ...(groupKey ? { groupKey } : {}) });

// Every screen the user may open, as flat leaves (group parents and section headers dropped).
// Visibility mirrors the existing More menu exactly: a group is shown by canSee, its children by visibleChildren.
export function flattenLeaves({ navItems, canSee, visibleChildren }) {
  const out = [];
  for (const item of navItems) {
    if (item.section) continue;
    if (!canSee(item)) continue;
    if (item.children) visibleChildren(item).forEach((c) => out.push(leafOf(c, item.key)));
    else out.push(leafOf(item));
  }
  return out;
}
// One category per navItems section, so the phone menu always matches the desktop sidebar and every
// screen appears exactly once. Settings → App View: "hidden" still hides, a saved order sorts within a category.
export function buildMoreSections({ navItems, canSee, visibleChildren, moreOrder: savedOrder, moreHidden: savedHidden }) {
  const moreOrder = savedOrder || []; // the layout passes null until Settings → App View has loaded
  const moreHidden = savedHidden || [];
  const sections = [];
  let cur = null;
  for (const item of navItems) {
    if (item.section) { cur = { title: CATEGORY_TITLES[item.section] || item.section, entries: [] }; sections.push(cur); continue; }
    if (!cur || !canSee(item)) continue;
    if (!item.children) {
      if (!moreHidden.includes(item.to)) cur.entries.push(leafOf(item));
      continue;
    }
    if (moreHidden.includes(item.key)) continue;
    const children = visibleChildren(item).map((c) => leafOf(c, item.key)).filter((c) => !moreHidden.includes(c.id));
    if (!children.length) continue;
    if (item.key === 'gtpl') {
      const main = GTPL_MAIN.map((id) => children.find((c) => c.id === id)).filter(Boolean);
      cur.entries.push(...main, ...children.filter((c) => !GTPL_MAIN.includes(c.id)));
    } else {
      cur.entries.push({ id: item.key, label: item.label, icon: item.icon, children });
    }
  }
  const rank = (e) => { const i = moreOrder.indexOf(e.id); return i < 0 ? Infinity : i; };
  if (moreOrder.length) sections.forEach((sec) => { if (sec.title !== 'GTPL') sec.entries.sort((x, y) => rank(x) - rank(y)); });
  return sections.filter((sec) => sec.entries.length);
}

// ── per-device pins and recent screens ─────────────────────────────────────────────────────
function read(key) {
  try { const v = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
}
function write(key, value) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private mode / storage full */ }
}

export const loadPins = () => read(PIN_KEY);
export const savePins = (pins) => write(PIN_KEY, pins);
export const loadRecent = () => read(RECENT_KEY);

export function togglePin(pins, id) {
  if (pins.includes(id)) return pins.filter((p) => p !== id);
  return pins.length >= MAX_PINS ? pins : [...pins, id];
}

export function rememberRecent(path) {
  const next = [path, ...read(RECENT_KEY).filter((p) => p !== path)].slice(0, MAX_RECENT);
  write(RECENT_KEY, next);
  return next;
}

// The screen a location belongs to (longest matching path wins), so /customers/123 counts as Customers.
export function leafForPath(leaves, pathname) {
  let best = null;
  for (const l of leaves) {
    const base = l.to.split('?')[0];
    if (pathname === base || pathname.startsWith(`${base}/`)) {
      if (!best || base.length > best.to.split('?')[0].length) best = l;
    }
  }
  return best;
}
